import { Box, Flex } from '@/components/atoms/Grid'
import { FC } from 'react'
import styled from 'styled-components'
import { transparentize } from 'polished'
import {
  HorizontalTitledProductsSliderFetcher,
  TitleType
} from '@/components/organisms/productsSlider/horizontalTitledProductsSlider'
import { H2 } from '@/components/atoms/typography'
import useTranslation from 'next-translate/useTranslation'
import useGraphQl from 'hooks/useGraphQl'
import { Sdk } from '@/network/graphql.g'
import { CloseIconButton } from '@/components/atoms/button'
import { convertAPIProductToProduct } from '@/types/bridgeProduct'
import { ProductCardActionType } from '../productCard/productCard'

import { HorizontalTitledProductsSlider } from '@/components/organisms/productsSlider'

type ProductsListingExpanderProps = {
  productId: string
  columnCount: number
  index: number
  indicatorIndex: number
  action: ProductCardActionType
  onClose: (index: number) => void
}

const ProductsListingExpander: FC<ProductsListingExpanderProps> = ({
  productId,
  columnCount,
  index,
  indicatorIndex,
  action,
  onClose
}) => {
  const { t } = useTranslation('common')
  const { requestNotifyError } = useGraphQl()

  const mayLikeFetcher =
    async (): Promise<HorizontalTitledProductsSliderFetcher> => {
      const result = await requestNotifyError(
        (sdk: Sdk) => ({
          method: sdk.getYouMayLikeProducts,
          variables: {
            productId
          }
        }),
        true
      )

      return {
        list: result.response?.recommended?.[0]?.listName,
        products:
          result.response?.recommended?.[0]?.products?.map((item) =>
            convertAPIProductToProduct(item)
          ) ?? [],
        algorithm: result.response?.recommended?.[0]?.algorithm
      }
    }

  return (
    <Flex flexDirection="column">
      <Flex justifyContent="space-around">
        {Array.from({ length: columnCount }, (v, i) => i).map((offset) => {
          return <Pointer key={offset} isVisible={indicatorIndex === offset} />
        })}
      </Flex>
      <ContentContainer p={20}>
        <Flex justifyContent="space-between" alignItems="center" pb={20}>
          <H2 m={0} p={0}>
            {t('Products.YouMayLike.title')}
          </H2>
          <CloseIconButton
            onClick={() => onClose(index)}
            showBackground={false}
          />
        </Flex>
        <HorizontalTitledProductsSlider
          titleType={TitleType.empty}
          slideItemsCount={{ desktop: 4, tablet: 3, mobile: 2 }}
          fetcher={mayLikeFetcher}
          swrKey={['recommended/YouMayLike', productId]}
          action={action}
          colors={{ background: '#f0f0f0' }}
        />
      </ContentContainer>
    </Flex>
  )
}

export default ProductsListingExpander

const Pointer = styled(Box)`
  width: 0;
  height: 0;
  border-left: 29px solid transparent;
  border-right: 29px solid transparent;
  border-bottom: 16px solid
    ${(props) => transparentize(0.94, props.theme.colors.onBackground)};
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
`

const ContentContainer = styled(Flex)`
  box-sizing: border-box;
  flex-direction: column;
  overflow: hidden;
  background-color: ${(props) =>
    transparentize(0.94, props.theme.colors.onBackground)};
  width: 100%;
`
